import { useRef, useState } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';

/** One waiting product on the order the scanned code could belong to. */
export interface AssignBarcodeOption {
  lineNo: number;
  sku: string;
  name: string;
  image?: string | undefined;
  /** The barcode the product already has; picking it replaces that one. */
  barcode?: string | undefined;
}

interface AssignBarcodeDialogProps {
  code: string;
  options: AssignBarcodeOption[];
  /** Preselected when only one waiting product has no barcode yet; the desk still confirms. */
  initial?: number | undefined;
  onPick: (lineNo: number) => void;
  onCancel: () => void;
  /** A scanner fired while the picker was open; its keys never pick or close anything. */
  onScanned: (code: string) => void;
  /** The code is a waybill or some other label, not a product: the screen takes it down its old dead end. */
  onNotProduct: () => void;
}

/**
 * A code no product carries, scanned with an order open, is usually the barcode of a product in that parcel that was
 * never registered (SSD-7677). The desk picks the product it is holding; nothing is saved without that pick, and a
 * product that already has a barcode says it will be replaced.
 */
export const AssignBarcodeDialog = ({
  code,
  options,
  initial,
  onPick,
  onCancel,
  onScanned,
  onNotProduct,
}: AssignBarcodeDialogProps) => {
  const [chosen, setChosen] = useState<number | undefined>(initial);
  const typed = useRef('');
  const dialogRef = useModalDialog(onCancel);
  const picked = options.find((option) => option.lineNo === chosen);

  return (
    <dialog
      className="wms-prompt assign-barcode"
      ref={dialogRef}
      aria-label="Which product is this barcode for?"
      // The picker has no text box, so a scanner's keys would land on whatever button has focus and its Enter
      // would press it. They are caught here instead: a code followed by Enter is a scan, never a pick.
      onKeyDown={(event) => {
        if (event.key.length === 1 && /[0-9A-Za-z]/.test(event.key) && !event.metaKey && !event.ctrlKey) {
          typed.current += event.key;
          event.preventDefault();
          return;
        }
        if (event.key === 'Enter' && typed.current) {
          event.preventDefault();
          const scanned = typed.current;
          typed.current = '';
          onScanned(scanned);
        }
      }}
    >
      <header>
        Which product is this barcode for?
        <button type="button" className="x" aria-label="Close" onClick={onCancel}>
          ✕
        </button>
      </header>
      <div className="body">
        <p className="mut">
          No product has <b>{code}</b> yet. Pick the product in your hand: the code is saved as its barcode and the line
          is inbounded.
        </p>
        <div className="assign-list" role="radiogroup" aria-label="Waiting products">
          {options.map((option) => (
            <button
              key={option.lineNo}
              type="button"
              role="radio"
              aria-checked={option.lineNo === chosen}
              className={`assign-option${option.lineNo === chosen ? ' on' : ''}`}
              onClick={() => setChosen(option.lineNo)}
            >
              <span className="thumb">{option.image ? <img src={option.image} alt="" /> : 'IMG'}</span>
              <span className="what">
                <b>{option.name}</b>
                <span className="mut">
                  {option.sku} · line {option.lineNo}
                  {option.barcode ? ` · has ${option.barcode}, will be replaced` : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onNotProduct}>
          Not a product barcode (waybill / other)
        </button>
        <button type="button" className="btn btn-line btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-blue btn-sm"
          disabled={!picked}
          onClick={() => picked && onPick(picked.lineNo)}
        >
          {picked?.barcode ? 'Replace barcode and inbound' : 'Save barcode and inbound'}
        </button>
      </div>
    </dialog>
  );
};
